import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { Button } from '@/components/generic';
import { ButtonVariant } from '@/components/generic/Button/button-config';
import { useAuth } from '@/contexts';
import { PUBLIC_PAGE_ROUTES } from '@/enums';
import { DetailsPagePassword } from '@/pages/SetPassword/DetailsPage';
import { LeftChevronBlackIcon as ArrowLeftIcon, DangerIcon, SolidCheckIcon } from '@/resources';
import { useConfirmEmailChangeMutation } from '@/services';

type ConfirmationStatus = 'pending' | 'success' | 'error';

export const ConfirmEmailChange = () => {
  const navigate = useNavigate();
  const [t] = useTranslation('common');
  const { user, logout } = useAuth();
  const [status, setStatus] = useState<ConfirmationStatus>('pending');
  const hasRequested = useRef(false);

  const { mutate: confirmEmailChange } = useConfirmEmailChangeMutation({
    onSuccess: () => {
      setStatus('success');
    },
    onError: () => {
      setStatus('error');
    }
  });

  useEffect(() => {
    if (hasRequested.current) {
      return;
    }
    hasRequested.current = true;

    const urlParams = new URLSearchParams(window.location.search);
    const token = urlParams.get('token');

    if (!token) {
      setStatus('error');
      return;
    }

    confirmEmailChange(token);
  }, [confirmEmailChange]);

  const handleBackToLogin = async () => {
    // The confirmed email invalidates the locally cached session, so force a clean
    // re-authentication instead of leaving a stale, soon-to-break session behind.
    if (user) {
      await logout();
    }
    navigate(PUBLIC_PAGE_ROUTES.LOGIN);
  };

  return (
    <DetailsPagePassword>
      <div className='z-[100] h-80 w-[90%] rounded-3xl bg-primary-white p-14 sm:w-2/6'>
        {status === 'pending' && (
          <div className='mb-5 flex justify-center text-2xl font-bold text-primary-blue'>
            {t('confirmingEmailChange')}
          </div>
        )}
        {status === 'success' && (
          <>
            <div className='mb-3 mt-0 flex justify-center'>
              <SolidCheckIcon className='h-9 w-10 justify-center' />
            </div>
            <div className='mb-5 flex justify-center text-2xl font-bold text-primary-blue'>
              {t('emailChangeConfirmed')}
            </div>
          </>
        )}
        {status === 'error' && (
          <>
            <div className='mb-3 mt-0 flex justify-center'>
              <DangerIcon className='h-9 w-10 justify-center' />
            </div>
            <div className='mb-5 flex justify-center text-center text-2xl font-bold text-primary-blue'>
              {t('emailChangeConfirmationFailed')}
            </div>
          </>
        )}
        {status !== 'pending' && (
          <div className='flex justify-center'>
            <Button
              variant={ButtonVariant.TERTIARY}
              handleOnClick={handleBackToLogin}
              className='mt-5 flex border-0'>
              <ArrowLeftIcon className='mr-2 text-base' />
              {t('backToLogin')}
            </Button>
          </div>
        )}
      </div>
    </DetailsPagePassword>
  );
};
